import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Text,
} from '@fluentui/react-components'
import { setCrossFocusAreaConfirm } from '../lib/api'

/**
 * The one dialog behind the cross-focus-area warning (#995).
 *
 * Acting on a demand outside your own focus area used to be refused outright.
 * It now warns: the backend answers 409 from a gate shared by twenty-three
 * routes, and the request goes through when it is re-sent confirmed. This
 * renders that confirmation once, for all of them — see `lib/api.ts` for why
 * the interception is central rather than per call site.
 *
 * The wording says what the person is about to do and what it costs, and
 * nothing else. It is not a scolding: the decision that opened this boundary
 * did so on purpose, and the recording — under the actor's name, on the
 * demand's timeline — is what makes it safe rather than the friction.
 */
export function CrossFocusAreaProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null)
  const resolveRef = useRef<((ok: boolean) => void) | null>(null)

  const answer = useCallback((ok: boolean) => {
    setMessage(null)
    resolveRef.current?.(ok)
    resolveRef.current = null
  }, [])

  useEffect(() => {
    setCrossFocusAreaConfirm(
      (text: string) =>
        new Promise<boolean>((resolve) => {
          // A second warning while one is open would strand the first
          // promise, and the request behind it would hang forever. Refuse
          // instead: the caller gets its 409 back and can be retried.
          if (resolveRef.current) {
            resolve(false)
            return
          }
          resolveRef.current = resolve
          setMessage(text)
        }),
    )
    return () => {
      setCrossFocusAreaConfirm(null)
      // Unmounting with a question outstanding must not leave the request
      // waiting on an answer nobody can give.
      resolveRef.current?.(false)
      resolveRef.current = null
    }
  }, [])

  return (
    <>
      {children}
      <Dialog open={message !== null} onOpenChange={(_, d) => !d.open && answer(false)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Outside your focus area</DialogTitle>
            <DialogContent>
              <Text>{message}</Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => answer(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={() => answer(true)}>
                Continue
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  )
}
